import global from "../global.js";
import Transform from "./Transform.js";

import Style from "../utils/Style.js";

const log = global.log.getLogger("f_mtac.scene.axis");
log.setLevel(log.levels.WARN);

// axis place on the screen
export const AXIS_NORTH = 0,
    AXIS_WEST = 1,
    AXIS_SOUTH = 2,
    AXIS_EAST = 3;

// frame.reference.position
export const DIRECTION_NORTH_WEST = 0, // standard
    DIRECTION_SOUTH_WEST = 1, // GIGN
    DIRECTION_SOUTH_EAST = 2,
    DIRECTION_NORTH_EAST = 3,
    DIRECTION_UNKNOWN = 4;

// axis types
export const AXIS_X = 0,
    AXIS_Y = 1,
    AXIS_UNKNOWN = 2;

/**
 * @brief Manage the axis of the Grid: [A,B,C,...] for X axis, [1,2,3...] for Y axis
 *        Axis is instanciated 4 times :
 *              . twice for X axes (top and bottom)
 *              . twice for Y axes (left and right)
 * The mechanism is different for an image and a GIS:
 * - for images, the data (list of coordinates) are created in draw() at the begining and don't change
 * - for GIS, the data is re-created after each transformation (scale or translate) and depends:
 *   . on the zoom level: apply a step if zoom is wide
 *   . on the translation: define coordinates only around the screen
 * @param {*} view
 * @param {*} svg
 * @param {*} conf
 * @param {*} layer
 * @param {*} type, AXIS_X or AXIS_Y
 * @param {*} direction, position of the axis example AXIS_NORTH for north
 */
export let Axis = function (view, svg, conf, layer, type, direction) {
    const style = new Style();
    this.type = AXIS_UNKNOWN;
    this.direction = DIRECTION_UNKNOWN;
    this.is_extended = false;
    let min_axis_x, min_axis_y;
    let max_axis_y;
    let reference_position; // do not init here, grid maybe come from another shared organization
    let range; // range and step for GIS coordinates
    let offset_x, offset_y;

    // Build axis coordinates
    // build_coords is an array of two functions
    // build_coords[0](min, max) builds the coordinates of the X axis (= letters)
    // build_coords[1](min, max) builds the coordinates of the Y axis (= numbers)
    // build_coords returns an array of coordinates
    let build_coords = [
        /**
         * @brief Build the list of coordinates of the X axis with letters: A, B, C, ..., Z, AA, AB, etc..
         *        If xmin is negative, a "-" is added : -AB, -AA, -Z, ..., -C, -B, -A
         * return array between Xmin and Xmax, ex: ["D", "E", "F", "G", "H"] if min = "D" and max = "H"
         **/
        (xmin, xmax, step) => {
            if (xmin == xmax) {
                // occurs when a geolocalized picture is all inside a square
                // otherwise nothing is drawn
                if (xmin > 0) {
                    xmin--;
                } else {
                    xmax++;
                }
            }

            if (step === undefined) {
                step = 1;
            }

            min_axis_x = xmin;
            //max_axis_x = xmax;
            //max_axis_x;

            let X = []; // x axis domain
            log.debug(`Axis.build_coords X min = ${xmin}, max = ${xmax}, step = ${step}`);

            // Negative part
            for (let n = xmin; n < Math.min(0, xmax); n++) {
                if (-n % step != 0) {
                    //log.debug(`Axis.build_coords1 x negative ignore n = ${n}`);
                    continue;
                }
                X.push("-" + this.number_to_letters(-n));
            }

            // Positive part
            for (let n = Math.max(0, xmin); n < xmax; n++) {
                if (n % step != 0) {
                    //log.debug(`Axis.build_coords1 x negative ignore n = ${n}`);
                    continue;
                }
                X.push(this.number_to_letters(n + 1));
            }

            log.debug("Axis.build_coords ", {X});

            switch (reference_position) {
                case DIRECTION_NORTH_EAST:
                case DIRECTION_SOUTH_EAST:
                    X = X.toString().split(",").reverse().join("");
                    break;
            }

            return X;
        },

        /**
         * @brief Build the list of coordinates of the Y axis with numbers: 0, 1, 2, 3, 4, etc..
         *        If xmin is negative, numbers are negative; -5, -4, -3, -2, -1
         */
        (ymin, ymax, step) => {
            min_axis_y = ymin;
            max_axis_y = ymax;

            if (step === undefined) {
                step = 1;
            }

            log.debug(`Axis.build_coords Y min = ${ymin}, max = ${ymax}, step = ${step}`);

            //log.debug("Axis.build_coords Y", {ymin, ymax});
            let Y = []; // y axis domain
            let ym = ymax;
            let i = 0;
            switch (reference_position) {
                case DIRECTION_NORTH_WEST: // standard
                case DIRECTION_NORTH_EAST:
                    if (global.scene.map.is_gis === false) {
                        // add all values between min and max, except 0
                        for (let n = 0; n <= ymax - ymin; n++) {
                            let value = ymin + n;
                            if (value != 0) {
                                Y[i++] = (ymin + n).toString();
                            }
                        }
                    } else {
                        // GIS with potential step
                        // add all values between min and max, except 0
                        // do not display 0
                        // expected result:
                        // ex step = 1:  -3, -2, -1, 1, 2, 3
                        // ex step = 2:  -6, -4, -2, 1, 3, 5
                        // ex step = 3:      -6, -3, 1, 4, 7
                        // ex step = 4:      -8, -4, 1, 5, 9
                        if (ymin > 0) {
                            // "0" is not displayed, so add +1
                            // ymin = 2 --> first coordinate is "3"
                            ymin++;
                        }

                        for (let n = ymin; n <= ymax; n++) {
                            // apply step: ignore coordinates when modulo != 0
                            //log.debug(`Axis.build_coords Y n = ${n}, continue ? ${n % step}, step = ${step}`);

                            if (step != 1) {
                                if (n < 0 && n % step != 0) {
                                    continue;
                                }

                                if (n > 0 && n % step != 1) {
                                    continue;
                                }
                            }

                            if (n != 0) {
                                // never add "0"
                                Y[i++] = n.toString();
                            }
                        }
                    }
                    break;

                case DIRECTION_SOUTH_WEST: // GIGN
                case DIRECTION_SOUTH_EAST:
                    if (global.scene.map.is_gis === false) {
                        // ad all values between min and max, except 0
                        for (let n = 0; n < ymax - ymin; n++) {
                            if (ym != 0) {
                                // never add "0"
                                Y[i++] = ym.toString();
                            }
                            ym--;
                        }
                    } else {
                        log.debug("Axis.build_coords GIS DIRECTION_SOUTH_EAST");

                        if (ymax < 0) {
                            // "0" is not displayed, so add +1
                            ymax--;
                        }

                        // add all values between min and max, except 0
                        for (let n = ymax; n >= ymin; n--) {
                            // apply step: ignore coordinates when modulo != 0
                            if (step != 1) {
                                if (n < 0 && n % step != 0) {
                                    continue;
                                }

                                if (n > 0 && n % step != 1) {
                                    continue;
                                }
                            }

                            if (n != 0) {
                                // never add "0"
                                Y[i++] = n.toString();
                            }
                        }
                    }
                    break;
            }
            log.debug("Axis.build_coords ", {Y});
            return Y;
        }
    ];

    // set this.direction
    switch (type) {
        case AXIS_X:
            switch (direction) {
                case AXIS_NORTH:
                case AXIS_SOUTH:
                    this.direction = direction;
                    break;
                default:
                    throw `Unknown x axis direction: ${direction}`;
            }
            break;
        case AXIS_Y:
            switch (direction) {
                case AXIS_WEST:
                case AXIS_EAST:
                    this.direction = direction;
                    break;
                default:
                    throw `Unknown y axis direction: ${direction}`;
            }
            break;
        default:
            throw `Unknown axis type: ${type}`;
    }

    this.type = type;
    this.data = null;

    // Creates CSS classes

    this.axis_classes = ["axis"];

    switch (type) {
        case AXIS_X:
            this.axis_classes.push("axis_x");
            break;
        case AXIS_Y:
            this.axis_classes.push("axis_y");
            break;
    }

    switch (direction) {
        case AXIS_NORTH:
            this.axis_classes.push("axis_north");
            break;
        case AXIS_WEST:
            this.axis_classes.push("axis_west");
            break;
        case AXIS_SOUTH:
            this.axis_classes.push("axis_south");
            break;
        case AXIS_EAST:
            this.axis_classes.push("axis_east");
            break;
    }

    this.axis = null;

    var QW, QH; // Quadrant width and Quadrant height

    /**
     * @brief Init coordinate ranges depending on is_extended, reference.position, etc...
     */
    this.init_data = function () {
        log.debug("Axis.init_data in ----------------", {is_extended: this.is_extended});
        log.debug("Axis.init_data", {type: type_to_string(), direction: direction_to_string()});

        reference_position = conf.get("frame.reference.position");
        if (this.is_extended === true) {
            if (global.scene.map.is_gis === true) {
                // for GIS
                range = this.compute_gis_range();
                this.data = build_coords[type](range.min, range.max, range.step);
            } else {
                // for picture, find the coordinates arround the picture
                range = this.compute_geo_picture_range();
                this.data = build_coords[type](range.min, range.max, 1); // step = 1, draw all coordinates
            }
        } else {
            // picture without geolocation -> basic grid
            const min = this.type === AXIS_X ? conf.get("axis.x.min") : conf.get("axis.y.min");
            const max = this.type === AXIS_X ? conf.get("axis.x.max") : conf.get("axis.y.max");
            this.data = build_coords[type](min, max, 1); // step = 1, draw all coordinates
        }
        log.debug("Axis.init_data in data[0]", {data0: this.data});
    };

    /**
     * Find the min and max square coordinates of the picture area, taking into account the picture can rotate
     * @return {min, max} axis coordinates
     */
    this.compute_geo_picture_range = function () {
        let min, max;
        let origin; // position of the rubik, in pixel
        let center; // center of the picture, in pixel
        let square_size; // size of the square, in pixel
        let size_max; // max size of the picture, in pixel

        // because the picture can rotate, we take the max of (width, height)
        size_max = Math.max(conf.get("grid.size.width"), conf.get("grid.size.height"));
        // we add 40% to the max size because if the picture is a square and the rotation is 45°,
        // then the diagonal size is the side multplied by square_root(2) = 1.4 = 1 + 40%
        size_max += size_max * 0.4;

        if (this.type === AXIS_X) {
            origin = conf.get("origin.x");
            square_size = conf.get("square.width");
            center = conf.get("grid.size.width") / 2;
            log.debug("Axis.compute_geo_picture_range IN AXIS_X", {origin_x: origin, position_x: conf.get("position.x")});
        }

        if (this.type === AXIS_Y) {
            origin = conf.get("origin.y");
            square_size = conf.get("square.height");
            center = conf.get("grid.size.height") / 2;
            log.debug("Axis.compute_geo_picture_range IN AXIS_Y", {origin_y: origin, position_y: conf.get("position.y")});
        }
        let min_pix = center - size_max / 2; //min pixel value of the picture, whatever its rotation
        let max_pix = center + size_max / 2; //max pixel value of the picture, whatever its rotation

        log.debug("Axis.compute_geo_picture_range", {type: type_to_string(), origin, square_size, size_max, center});

        ////////////////////////////////////
        // find min = find square including the value 0. 0 is the top left for a picture
        ////////////////////////////////////

        // if standard grid
        if (this.type === AXIS_X || reference_position == DIRECTION_NORTH_WEST) {
            min = 0;
            if (origin >= min_pix) {
                while (origin > min_pix) {
                    origin -= square_size;
                    min--;
                }
            } else {
                while (origin < min_pix) {
                    origin += square_size;
                    min++;
                }
            }
        }

        // if GIGN
        else {
            min = 0;
            if (origin <= max_pix) {
                while (origin <= max_pix) {
                    origin += square_size;
                    min--;
                }
            } else {
                while (origin > max_pix) {
                    origin -= square_size;
                    min++;
                }
            }
        }

        ////////////////////////////////////
        // find max = find square including the value l
        ////////////////////////////////////

        // reinit the origin
        if (this.type === AXIS_X) {
            origin = conf.get("origin.x");
        }
        if (this.type === AXIS_Y) {
            origin = conf.get("origin.y");
        }

        if (this.type === AXIS_X || reference_position == DIRECTION_NORTH_WEST) {
            // standard
            max = 0;
            if (origin >= max_pix) {
                while (origin > max_pix) {
                    //log.debug(`Axis.compute_geo_picture_range AXIS_X DIRECTION_NORTH_WEST origin = ${origin} max = ${max} max_pix = ${max_pix} square_size = ${square_size}`);
                    origin -= square_size;
                    max--;
                }
            } else {
                while (origin < max_pix) {
                    origin += square_size;
                    max++;
                }
            }
        } else {
            // AXIS_Y of GIGN
            max = 0;
            if (origin >= min_pix) {
                while (origin >= min_pix) {
                    origin -= square_size;
                    max++;
                }
            } else {
                while (origin < min_pix) {
                    origin += square_size;
                    max--;
                }
            }
        }
        log.debug("Axis.compute_geo_picture_range out", {type: type_to_string(), min, max});
        return {min, max};
    };

    /**
     * Find the min and max square coordinates of the GIS, taking into account the actual view
     * @return min: the minimum square visible in the screen,
     *              AXIS_X: value 0 = coord "A"
     *              AXIS_Y: value 0 = coord "1"
     *         max: the maximum square visible in the screen
     *         delta_coord: the minimum coord to dispaly (depending on the step)
     *         step: the step between two coordinates to display
     */
    this.compute_gis_range = function () {
        let min, max, step, delta_coord;
        let origin; // position of the rubik, in pixel
        let square_size; // size of the square, in pixel

        // get the minimum and maximum coordinates in D3
        let top_left = global.scene.transform_coordinates_3({x: 0, y: 0});
        let bottom_right = global.scene.transform_coordinates_3({
            x: global.view.width,
            y: global.view.height
        });

        // Find the min and max squares

        if (this.type === AXIS_X) {
            origin = conf.get("origin.x");
            square_size = conf.get("square.width");
            //let width = conf.get("grid.common_size.width");

            // find the number of the leftest square of the screen
            min = Math.floor((top_left.x - origin) / square_size);

            // find the number of the rightest square of the screen
            max = Math.floor((bottom_right.x - origin) / square_size) + 1;

            // find number of squares visible in the screen
            let nb_square = max - min;

            // find the step to apply between each written coordinates (to avoid too many coordinates)
            step = Math.floor((bottom_right.x - top_left.x) / (square_size * 20));
            if (step < 1) {
                step = 1;
            }
            // example: if there are 19 squares, step = 1, draw every coordinate
            // example: if there are 100 squares, step = 5, draw one coordinate each 5 coordinates

            // find the delta_coord taking into account that coordinate "A" shall be drawn
            // use the modulo
            log.debug(`Axis.compute_gis_range  AXIS_X min = ${min}, nb_square = ${nb_square}, step = ${step}`);
            if (step == 1) {
                delta_coord = 0;
            } else {
                if (min < 0) {
                    delta_coord = -min % step;
                } else {
                    if (min % step == 0) {
                        delta_coord = 0;
                    } else {
                        delta_coord = step - (min % step);
                    }
                }
            }

            log.debug(`Axis.compute_gis_range out AXIS_X min = ${min}, max = ${max}, step = ${step}, delta_coord = ${delta_coord}`);

            // find the max taking into account that coordinate "A" shall be drawn
            // use the modulo
            //max = max - nb_square % step;
        } else {
            // type == AXIS_Y

            // standard grid
            if (reference_position == DIRECTION_NORTH_WEST) {
                origin = conf.get("origin.y");
                square_size = conf.get("square.height");
                //let width = conf.get("grid.common_size.width");

                // find the number of the upper square of the screen
                min = Math.floor((top_left.y - origin) / square_size);

                // find the number of the downest square of the screen
                max = Math.floor((bottom_right.y - origin) / square_size) + 1;

                // find number of squares visible in the screen
                let nb_square = max - min;

                // find the step to apply between each written coordinates (to avoid too many coordinates)
                step = Math.floor(nb_square / 20) + 1;
                // example: if there are 19 squares, step = 1, draw every coordinate
                // example: if there are 100 squares, step = 5, draw one coordinate each 5 coordinates

                // find the min taking into account that coordinate "A" shall be drawn
                // use the modulo
                log.debug(`Axis.compute_gis_range  AXIS_Y min = ${min}, max = ${max} nb_square = ${nb_square}, step = ${step}`);
                // do not display 0
                // ex step = 2:  -6, -4, -2, 1, 3, 5
                // ex step = 3:      -6, -3, 1, 4, 7
                // ex step = 4:      -8, -4, 1, 5, 9
                if (step == 1) {
                    delta_coord = 0;
                } else {
                    if (min < 0) {
                        delta_coord = -min % step;
                    } else {
                        // can't find an explication for this
                        // found after several tries
                        if (min % step == 0) {
                            delta_coord = 0;
                        } else {
                            delta_coord = step - (min % step);
                        }
                    }
                }
            }
            // GIGN grid
            else if (reference_position == DIRECTION_SOUTH_WEST) {
                origin = conf.get("origin.y");
                square_size = conf.get("square.height");
                //let width = conf.get("grid.common_size.width");

                // find the number of the upper square of the screen
                max = Math.floor((origin - top_left.y) / square_size);

                // find the number of the downest square of the screen
                min = Math.floor((origin - bottom_right.y) / square_size) + 1;

                // find number of squares visible in the screen
                let nb_square = max - min;

                // find the step to apply between each written coordinates (to avoid too many coordinates)
                step = Math.floor(nb_square / 30) + 1;
                // example: if there are 19 squares, step = 1, draw every coordinate
                // example: if there are 100 squares, step = 5, draw one coordinate each 5 coordinates

                // find the min taking into account that coordinate "A" shall be drawn
                // use the modulo
                log.debug(`Axis.compute_gis_range AXIS_Y min = ${min} max = ${max} nb_square = ${nb_square}, step = ${step}`);
                if (step == 1) {
                    delta_coord = 0;
                } else {
                    // can't find an explication for this
                    // found after several tries
                    if (max < 0) {
                        delta_coord = (-max % step) - (step - 1);
                    } else {
                        delta_coord = -(max + step - 1) % step;
                    }
                }
            }

            log.debug(`Axis.compute_gis_range out AXIS_Y min = ${min}, max = ${max}, step = ${step}, delta_coord = ${delta_coord}`);
        }

        return {min, max, step, delta_coord};
    };

    /**
     * Draw the axis
     */
    this.draw = function () {
        log.debug("Axis.draw IN", {type: type_to_string(), direction: direction_to_string()});

        const GC = conf.get("grid.color");
        const FO = conf.get("axis.font.opacity");

        const axis_selector = "g." + this.axis_classes.join(".");

        this.axis = layer.select(axis_selector);

        style.rule_set(
            axis_selector,
            `
            ${axis_selector} {
                fill: transparent;
            }
            ${axis_selector} text {
                font-size: calc(1 / 3 * var(--wb));
                font-weight: 500;
                opacity: ${FO};
                dominant-baseline: middle;
                text-anchor: middle;
                fill: ${GC};
            }
            ${axis_selector} text {
                transform: translate(
                    ${this.direction === AXIS_WEST ? "calc(1/4 * var(--wb))" : this.direction === AXIS_EAST ? "calc(-1/4 * var(--wb))" : "0"}, 
                    ${this.direction === AXIS_NORTH ? "calc(1/4 * var(--wb))" : this.direction === AXIS_SOUTH ? "calc(-1/4 * var(--wb))" : "0"}
                );
            }
        `
        );

        // sub_axis style added here so it is not duplicated for each sub-grid
        const sub_axis_selector = "g#sub_axis g." + this.axis_classes.join(".") + "";
        style.rule_set(
            sub_axis_selector,
            `
            ${sub_axis_selector} {
                fill: transparent;
            }
            ${sub_axis_selector} text {
                font-size: calc(1 / 4 * var(--wb));
                font-weight: 500;
                opacity: ${FO};
                dominant-baseline: middle;
                text-anchor: middle;
                fill: ${GC};
            }
        `
        );

        // define and create coordinate coords for images (extended or not)
        if (global.scene.map.is_gis === false) {
            // if picture without geolocation -> basic grid
            if (this.is_extended === false) {
                // recompute min and max each time --> useful during grid move
                const min = this.type === AXIS_X ? conf.get("axis.x.min") : conf.get("axis.y.min");
                const max = this.type === AXIS_X ? conf.get("axis.x.max") : conf.get("axis.y.max");
                this.data = build_coords[this.type](min, max);
            }

            define_image_coords();
        } else {
            // Note: for GIS, coords are redefined after each transformation (scale or translate)
            // in order to limit the number of coords
            define_gis_coords();
        }

        if (this.axis.size() === 0) {
            this.axis = layer.append("g").attr("class", this.axis_classes.join(" "));
        } else {
            this.axis.html("");
        }

        this.axis_text = this.axis.append("g");
        //log.debug("Axis.draw x_coord_offset", this.axis_text);
        if (this.data != null) {
            // maybe data is not ready yet
            this.axis_text
                .selectAll("text")
                .data(this.data)
                .enter()
                .append("text")
                .text(function (d) {
                    return d;
                });
        }

        this.transform.refresh();
    };

    /**
     * @brief Draw the coordinate coords for an image, with or without geo-location.
     * Create _coord_offset() that will update the drawing depending on k
     */
    const define_image_coords = () => {
        const SW = conf.get("square.width"),
            SH = conf.get("square.height");

        let OX = conf.get("origin.x");
        let OY = conf.get("origin.y");

        QW = conf.get("quadrant.width");
        QH = conf.get("quadrant.height");

        //const ARX = conf.get("axis.x.range");
        const ARY = conf.get("axis.y.range");

        switch (conf.get("frame.reference.position")) {
            case DIRECTION_NORTH_WEST: // standard
                offset_x = OX;
                offset_y = OY;
                break;
            case DIRECTION_SOUTH_WEST: // GIGN
                offset_x = OX;
                offset_y = OY - ARY * SH; // short offset to apply for centering the axis value in the square
                break;
        }

        // axis coords positions (pixel)
        if (this.is_extended === true) {
            // picture geolocalized -> extended grid but axis limited to picture area
            ///////////////////////////

            this.x_coord_offset = (k) => {
                return this.type === AXIS_X
                    ? (_, n) => {
                          let _o = (SW / 2 + offset_x + (n + min_axis_x) * SW) * k;
                          //log.debug(`Axis.draw.x_coord_offset n= ${n}, k= ${k}, offset_x = ${offset_x}, min_axis_x = ${min_axis_x}, offset = ${_o} `);
                          return _o;
                      }
                    : null;
            };

            this.y_coord_offset = (k) =>
                this.type === AXIS_X
                    ? null
                    : (_, m) => {
                          if (reference_position == DIRECTION_NORTH_WEST) {
                              // standard
                              if (min_axis_y < 0) {
                                  let _o = (SH / 2 + offset_y + (m + min_axis_y) * SH) * k;
                                  //log.debug(`Axis.draw.y_coord_offset m= ${m}, k= ${k}, offset_y = ${offset_y}, offset = ${_o}`);
                                  return _o;
                              } else {
                                  // subsctract 1 (min_axis_y - 1) because theres is no 0 on the Y axis (...,-2,-1,1,2,3,...)
                                  let _o = (SH / 2 + offset_y + (m + min_axis_y - 1) * SH) * k;
                                  //log.debug(`Axis.draw.y_coord_offset m= ${m}, k= ${k}, offset_y = ${offset_y}, offset = ${_o}`);
                                  return _o;
                              }
                          }
                          if (reference_position == DIRECTION_SOUTH_WEST) {
                              // GIGN
                              if (max_axis_y > 0) {
                                  let _o = (SH / 2 + OY + (m - max_axis_y) * SH) * k;
                                  //log.debug(`Axis.draw.y_coord_offset m= ${m}, k= ${k}, OY = ${OY}, offset = ${_o}`);
                                  return _o;
                              } else {
                                  let _o = (SH / 2 + OY + (m - max_axis_y - 1) * SH) * k;
                                  //log.debug(`Axis.draw.y_coord_offset m= ${m}, k= ${k}, OY = ${OY}, offset = ${_o}`);
                                  return _o;
                              }
                          }
                      };
        } else {
            // case of basic picture without geo-localization
            this.x_coord_offset = (k) => (this.type === AXIS_X ? (_, n) => (SW / 2 + offset_x + n * SW) * k : null);
            this.y_coord_offset = (k) => (this.type === AXIS_X ? null : (_, m) => (SH / 2 + offset_y + m * SH) * k);
            //log.debug("Axis.draw x_coord_offset, y_coord_offset", {x_coord_offset,y_coord_offset});
        }
    };

    /**
     * @brief Draw the coordinate coords for the GIS
     * Create _coord_offset() that will update the drawing depending on k
     */
    const define_gis_coords = () => {
        // use SW and SH to display the coord in the middle of the square
        const SW = conf.get("square.width"),
            SH = conf.get("square.height");

        let OX = conf.get("origin.x");
        let OY = conf.get("origin.y");

        log.debug("Axis.define_gis_coords IN", {type: type_to_string(), direction: direction_to_string()});

        //const ARY = conf.get("axis.y.range");
        log.debug(`Axis.define_gis_coords OY = ${OY}`);

        // AXIS_X coords positions

        this.x_coord_offset = (k) => {
            return this.type === AXIS_X
                ? (_, n) => {
                      let min = range.min + range.delta_coord;
                      let _o = (SW / 2 + OX + (n * range.step + min) * SW) * k;
                      //log.debug(`Axis.define_gis_coords.x_coord_offset n= ${n} k= ${k} min = ${min} offset = ${_o} range: min = ${range.min}, step = ${range.step}, SW = ${SW}`);
                      return _o;
                  }
                : null;
        };

        // AXIS_Y coords positions

        if (reference_position == DIRECTION_NORTH_WEST) {
            // standard grid
            this.y_coord_offset = (k) =>
                this.type === AXIS_X
                    ? null
                    : (_, m) => {
                          let min = range.min + range.delta_coord;
                          let _o = (SH / 2 + OY + (m * range.step + min) * SH) * k;
                          //log.debug(`Axis.define_gis_coords.y_coord_offset m= ${m} k= ${k} OY = ${OY} min = ${min} offset = ${_o} step = ${range.step}, SW = ${SW}`);
                          return _o;
                      };
        }
        if (reference_position == DIRECTION_SOUTH_WEST) {
            // gign grid
            this.y_coord_offset = (k) =>
                this.type === AXIS_X
                    ? null
                    : (_, m) => {
                          let max = range.max + range.delta_coord;
                          let _o = (SH / 2 + OY - (max - m * range.step) * SH) * k;
                          //log.debug(`Axis.define_gis_coords.y_coord_offset m= ${m} k= ${k} OY = ${OY} max = ${max} offset = ${_o} step = ${range.step}, SW = ${SW}`);
                          return _o;
                      };
        }
    };

    /**
     * @brief Computes axes translation during transformation
     * @param {*} x x translation
     * @param {*} y y translation
     * @param {*} k zoom factor
     * @returns
     */
    let axis_translate = (x, y, k) => {
        var {top, left, bottom, right} = view.margin;
        var H = svg.height,
            W = svg.width;
        var dx = 0,
            dy = 0;

        if (this.is_extended === true) {
            // Extended grid
            ////////////////

            if (this.type === AXIS_X) {
                if (this.direction === AXIS_NORTH) {
                    // stay on top and move horizontally
                    dx = x;
                    dy = top;
                } else if (this.direction === AXIS_SOUTH) {
                    // stay on bottom and move horizontally
                    dx = x;
                    dy = H - bottom;
                }
                return `translate(${dx},${dy})`;
            } else if (this.type === AXIS_Y) {
                if (this.direction === AXIS_WEST) {
                    // stay on left and move vertically
                    dx = left;
                    dy = y;
                } else if (this.direction === AXIS_EAST) {
                    // stay on right and move vertically
                    dx = W - right;
                    dy = y;
                }
                return `translate(${dx},${dy})`;
            }
        } else {
            // none-extended grid for basic picture
            ////////////////

            if (this.type === AXIS_X) {
                if (this.direction === AXIS_NORTH) {
                    dx = x;
                    dy = Math.max(top, y);
                } else if (this.direction === AXIS_SOUTH) {
                    dx = x;
                    dy = Math.min(H - bottom, y + QH * k);
                }
                return `translate(${dx},${dy})`;
            } else if (this.type === AXIS_Y) {
                if (this.direction === AXIS_WEST) {
                    dx = Math.max(left, x);
                    dy = y;
                } else if (this.direction === AXIS_EAST) {
                    dx = Math.min(W - right, x + QW * k);
                    dy = y;
                }
                return `translate(${dx},${dy})`;
            }
        }
    };

    this.transform = new Transform({x: 0, y: 0, k: 1});

    /**
     * @brief callback on D3 translation, build the transform atribute
     * @param x,y: translation
     * @param k: zoom factor
     */
    this.transform.on_translate(({x, y, k}) => {
        log.debug(`Axis.transform.on_change k= ${k}`);

        // For GIS, redraw the axes, find min, max, step
        // and recreate html elements
        if (global.scene.map.is_gis === true) {
            on_translate_gis(k);
        }

        this.axis_text.attr("transform", `${axis_translate(x, y, k)}`);
    });

    /**
     * @brief callback on D3 zoom
     */
    this.transform.on_scale(({k}) => {
        log.debug(`Axis.transform.on_scale k= ${k}`);
        if (this.x_coord_offset !== undefined && this.y_coord_offset !== undefined) {
            if (global.scene.map.is_gis === true) {
                redraw_gis();
            }

            // apply k to the coords
            this.axis_text.selectAll("text").attr("x", this.x_coord_offset(k)).attr("y", this.y_coord_offset(k));
        }
    });

    /**
     * @brief redraw the axis for GIS:
     *   re-init the range
     *   create the coordinate elements with eventual step
     * @param {*} k, D3 scale factor
     */
    const on_translate_gis = (k) => {
        redraw_gis();

        this.axis_text.selectAll("text").attr("x", this.x_coord_offset(k)).attr("y", this.y_coord_offset(k));
    };

    /**
     * @brief redraw the axis:
     *   re-init the range
     *   create the coordinate elements with eventual step
     * @param {*} k
     */
    const redraw_gis = () => {
        this.init_data(); // build data[]

        this.axis_text.html(null);
        define_gis_coords();
        log.debug("Axis.transform.on_scale", this.data);
        if (this.data != null) {
            this.axis_text
                .selectAll("text")
                .data(this.data)
                .enter()
                .append("text")
                .text(function (d) {
                    return d;
                });
        }
    };

    this.number_to_letters = (val, tail = "") => {
        if (val <= 26) {
            return `${String.fromCharCode(val + 64)}${tail}`;
        }

        const remainder = val % 26 || 26;
        const division = Math.trunc(val / 26) - (remainder === 26 ? 1 : 0);

        return this.number_to_letters(division, `${String.fromCharCode(remainder + 64)}${tail}`);
    };

    /**
     * @brief convert int dirtection to string direction
     * @returns string
     */
    const direction_to_string = () => {
        switch (this.direction) {
            case AXIS_NORTH:
                return "AXIS_NORTH";
            case AXIS_SOUTH:
                return "AXIS_SOUTH";
            case AXIS_WEST:
                return "AXIS_WEST";
            case AXIS_EAST:
                return "AXIS_EAST";
            default:
                return "unknown";
        }
    };

    /**
     * @brief convert int type to string type for logs
     * @returns string
     */
    const type_to_string = () => {
        switch (this.type) {
            case AXIS_X:
                return "AXIS_X";
            case AXIS_Y:
                return "AXIS_Y";
            default:
                return "unknown";
        }
    };
};
